import { nextTick, onMounted, ref, watch, type Ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';

type UseTextareaAutoGrowOptions = {
  autoGrow: boolean;
  autoGrowMinHeight?: number;
  autoGrowMaxRows?: number;
  model: Ref<string>;
};

export function useTextareaAutoGrow(
  textareaRef: Ref<HTMLTextAreaElement | null>,
  fieldRef: Ref<HTMLElement | null>,
  { autoGrow, autoGrowMinHeight, autoGrowMaxRows, model }: UseTextareaAutoGrowOptions
) {
  const userPreferredHeight = ref<number | null>(null);
  let adjusting = false;
  let pendingHeightAdjustments = 0;

  function getMinHeight(el: HTMLTextAreaElement) {
    if (autoGrowMinHeight != null) {
      return autoGrowMinHeight;
    }

    return parseFloat(getComputedStyle(el).minHeight) || 0;
  }

  function getHeightLimit() {
    const el = textareaRef.value;
    const fieldEl = fieldRef.value;
    const limitEl = fieldEl?.closest('.ui-field');
    const lineHeight = el ? Number.parseFloat(getComputedStyle(el).lineHeight) : Number.NaN;
    const rowsLimit = autoGrowMaxRows != null && Number.isFinite(lineHeight) ? lineHeight * autoGrowMaxRows : null;

    if (!fieldEl || !limitEl) {
      return rowsLimit;
    }

    const limitStyle = getComputedStyle(limitEl);
    const maxHeight = Number.parseFloat(limitStyle.maxHeight);

    if (!Number.isFinite(maxHeight)) {
      return rowsLimit;
    }

    const fieldStyle = getComputedStyle(fieldEl);
    const available =
      Math.min(limitEl.clientHeight, maxHeight) -
      Number.parseFloat(limitStyle.paddingTop) -
      Number.parseFloat(limitStyle.paddingBottom);

    const fieldLimit =
      available - Number.parseFloat(fieldStyle.paddingTop) - Number.parseFloat(fieldStyle.paddingBottom);

    return rowsLimit == null ? fieldLimit : Math.min(fieldLimit, rowsLimit);
  }

  function measureContentHeight(el: HTMLTextAreaElement) {
    const minHeight = getMinHeight(el);

    el.style.height = 'auto';
    el.style.minHeight = `${minHeight}px`;

    return Math.max(el.scrollHeight, minHeight);
  }

  function adjustHeight() {
    const el = textareaRef.value;

    if (!el || !autoGrow) {
      return;
    }

    adjusting = true;

    const limit = getHeightLimit();

    if (limit != null && limit <= 0) {
      adjusting = false;

      if (pendingHeightAdjustments < 5) {
        pendingHeightAdjustments += 1;
        nextTick(adjustHeight);
      }

      return;
    }

    pendingHeightAdjustments = 0;

    const minHeight = getMinHeight(el);
    const contentHeight = measureContentHeight(el);

    if (userPreferredHeight.value != null && userPreferredHeight.value <= contentHeight) {
      userPreferredHeight.value = null;
    }

    let height = Math.max(contentHeight, userPreferredHeight.value ?? 0);

    if (limit != null) {
      height = Math.min(height, limit);
      el.style.maxHeight = `${limit}px`;
      el.style.minHeight = `${minHeight}px`;

      if (fieldRef.value) {
        fieldRef.value.style.maxHeight = '';
      }
    } else {
      el.style.maxHeight = 'none';
      el.style.minHeight = `${minHeight}px`;

      if (fieldRef.value) {
        fieldRef.value.style.maxHeight = 'none';
      }
    }

    el.style.height = `${height}px`;
    el.style.overflowY = limit != null && el.scrollHeight > height ? 'auto' : 'hidden';

    adjusting = false;
  }

  function syncManualHeight() {
    const el = textareaRef.value;

    if (!el || !autoGrow || adjusting) {
      return;
    }

    const currentHeight = el.clientHeight;
    const contentHeight = measureContentHeight(el);

    el.style.height = `${currentHeight}px`;
    userPreferredHeight.value = currentHeight > contentHeight ? currentHeight : null;
  }

  useResizeObserver(textareaRef, syncManualHeight);

  useResizeObserver(
    () =>
      autoGrow
        ? ((fieldRef.value?.closest('.ui-field') ?? fieldRef.value?.parentElement ?? null) as HTMLElement | null)
        : null,
    adjustHeight
  );

  watch(model, () => nextTick(adjustHeight), { flush: 'post', immediate: true });

  onMounted(() => {
    nextTick(adjustHeight);
  });

  return { adjustHeight };
}
